import { Tooltip } from 'antd';
import styles from './VMCCompareTable.module.scss';

const VMCCompareTable = () => {
  // Table data structure for easier maintenance
  const features = [
    {
      name: 'Blue Checkmark Display',
      vmc: {
        value:
          'Displays a blue verification checkmark next to the logo in Gmail, Yahoo, Apple Mail, and more.',
        isPositive: true,
      },
      cmc: {
        value: 'Does not display a blue checkmark.',
        isPositive: false,
      },
    },
    {
      name: 'Trust and Brand Impact',
      vmc: {
        value:
          'Provides maximum brand trust and credibility with verified trademark logos and blue checkmarks.',
        isPositive: true,
      },
      cmc: {
        value:
          'Boosts brand recognition by displaying the logo in Gmail, but without the blue checkmark.',
        isPositive: true,
      },
    },
    {
      name: 'Logo Requirements',
      vmc: {
        value: 'Must have a registered trademark or official government seal.',
        isPositive: true,
      },
      cmc: {
        value: 'No trademark required; logo must have been in use for at least 12 months.',
        isPositive: false,
      },
    },
    {
      name: 'Security and Fraud Prevention',
      vmc: {
        value:
          'Strong protection against phishing and spoofing through extended validation and DMARC enforcement.',
        isPositive: true,
      },
      cmc: {
        value: 'Moderate protection; DMARC enforcement required but less stringent validation.',
        isPositive: true,
      },
    },
    {
      name: 'Supported Email Platforms',
      vmc: {
        value: 'Gmail, Yahoo, Apple Mail, Fastmail, ZohoMail, Cloudmark.',
        isPositive: true,
      },
      cmc: {
        value: 'Currently only supported by Gmail.',
        isPositive: false,
      },
    },
    {
      name: 'Implementation Time',
      vmc: {
        value: 'Longer process due to trademark verification and extended validation.',
        isPositive: false,
      },
      cmc: {
        value: 'Faster setup; requires only proof of logo usage.',
        isPositive: true,
      },
    },
    {
      name: 'Cost',
      vmc: {
        value: 'Higher cost due to trademark verification and comprehensive security checks.',
        isPositive: false,
      },
      cmc: {
        value: 'More cost-effective; no trademark registration required.',
        isPositive: true,
      },
    },
    {
      name: 'Logo Flexibility',
      vmc: {
        value: 'No modifications allowed once the logo is verified.',
        isPositive: false,
      },
      cmc: {
        value: 'Allows minor logo modifications for flexibility.',
        isPositive: true,
      },
    },
    {
      name: 'Coverage and Domain Flexibility',
      vmc: {
        value: 'Covers one primary logo per domain and allows up to 249 additional domains.',
        isPositive: true,
      },
      cmc: {
        value:
          'Same domain flexibility as VMC, covering one logo and up to 249 additional domains.',
        isPositive: true,
      },
    },
    {
      name: 'Renewal and Maintenance',
      vmc: {
        value: 'Must maintain DMARC enforcement and trademark status.',
        isPositive: true,
      },
      cmc: {
        value: 'Must maintain DMARC and logo usage history.',
        isPositive: true,
      },
    },
    {
      name: 'Target Audience',
      vmc: {
        value:
          'Established brands with registered trademarks or government entities seeking maximum brand visibility and security.',
        isPositive: true,
      },
      cmc: {
        value:
          'Startups, SMBs, or organizations without a trademark seeking cost-effective logo visibility in Gmail.',
        isPositive: true,
      },
    },
  ];

  // Helper function to render check or x icon with inline text
  const renderIconWithText = (text, isPositive) => (
    <div className="inner-container">
      <div className="image-wrapper circle-image">
        <img
          src={isPositive ? '/images/icon-check-green-bg.svg' : '/images/icon-x.svg'}
          loading="eager"
          alt={isPositive ? 'Yes' : 'No'}
          className="image cover"
        />
      </div>
      <span>{text}</span>
    </div>
  );

  // Render the first column with feature names
  const renderFeatureColumn = () => (
    <div className="pricing-column first">
      <div className="pricing-table-top first">
        <div className="text-400 bold color-neutral-800">Feature</div>
      </div>
      {features.map((feature, index) => (
        <div key={index} className="pricing-content-wrapper left">
          <div className="text-300 color-neutral-800">
            <Tooltip className="tooltip" title={feature.name}>
              {feature.name}
            </Tooltip>
          </div>
        </div>
      ))}
    </div>
  );

  // Render VMC column
  const renderVMCColumn = () => (
    <div className="pricing-column">
      <div className="pricing-table-top">
        <h2 className="text-500 bold color-neutral-800 product-heading">
          Verified Mark Certificate (VMC)
        </h2>
      </div>
      {features.map((feature, index) => (
        <div key={index} className="pricing-content-wrapper">
          <div className="text-200 paragraph-medium medium">
            {renderIconWithText(feature.vmc.value, feature.vmc.isPositive)}
          </div>
        </div>
      ))}
    </div>
  );

  // Render CMC column
  const renderCMCColumn = () => (
    <div className="pricing-column">
      <div className="pricing-table-top">
        <h2 className="text-500 bold color-neutral-800 product-heading">
          Common Mark Certificate (CMC)
        </h2>
      </div>
      {features.map((feature, index) => (
        <div key={index} className="pricing-content-wrapper">
          <div className="text-200 paragraph-medium medium">
            {renderIconWithText(feature.cmc.value, feature.cmc.isPositive)}
          </div>
        </div>
      ))}
    </div>
  );

  // Render mobile cards
  const renderMobileCards = () => (
    <div className={styles.vmcMobileCards}>
      {features.map((feature, index) => (
        <div key={index} className="vmc-feature-card">
          <div className="vmc-card-header">
            <h3>{feature.name}</h3>
          </div>
          <div className="vmc-card-body">
            <div className="vmc-comparison-row">
              <div className="vmc-comparison-cell">
                <div className="vmc-cell-title">VMC</div>
                <div className="vmc-cell-content">
                  <div className="image-wrapper circle-image">
                    <img
                      src={
                        feature.vmc.isPositive
                          ? '/images/icon-check-green-bg.svg'
                          : '/images/icon-x.svg'
                      }
                      loading="eager"
                      alt={feature.vmc.isPositive ? 'Yes' : 'No'}
                      className="image cover"
                    />
                  </div>
                  <span>{feature.vmc.value}</span>
                </div>
              </div>
              <div className="vmc-comparison-cell">
                <div className="vmc-cell-title">CMC</div>
                <div className="vmc-cell-content">
                  <div className="image-wrapper circle-image">
                    <img
                      src={
                        feature.cmc.isPositive
                          ? '/images/icon-check-green-bg.svg'
                          : '/images/icon-x.svg'
                      }
                      loading="eager"
                      alt={feature.cmc.isPositive ? 'Yes' : 'No'}
                      className="image cover"
                    />
                  </div>
                  <span>{feature.cmc.value}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="overflow-hidden border-radius-40px---bottom">
      <section className="section pd-top-0px bg-neutral-100">
        <div className="container-default w-container">
          <div className="inner-container _600px---mbl center">
            <div className="inner-container _745px center">
              <div className="text-center mg-bottom-40px">
                <h2 className="display-2">CMC vs. VMC Comparison</h2>
                <p>
                  Compare the features and benefits of Common Mark Certificates (CMC) and Verified
                  Mark Certificates (VMC) to determine which solution best fits your organization's
                  needs.
                </p>
              </div>
            </div>

            {/* Desktop table layout */}
            <div className={`w-layout-grid pricing-tablet ${styles.vmcCompareTable}`}>
              {renderFeatureColumn()}
              {renderCMCColumn()}
              {renderVMCColumn()}
            </div>

            {/* Mobile card layout */}
            {renderMobileCards()}
          </div>
        </div>
      </section>
    </div>
  );
};

export default VMCCompareTable;
